import { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar } from "recharts";

const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function ResultStat({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl sm:text-3xl font-semibold mt-1.5 ${accent ? "text-emerald-600" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function SipCalc() {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);

  const { data, invested, future } = useMemo(() => {
    const arr = [];
    let inv = 0;
    let val = 0;
    const r = rate / 100 / 12;
    for (let y = 1; y <= years; y++) {
      for (let m = 0; m < 12; m++) {
        val = (val + monthly) * (1 + r);
        inv += monthly;
      }
      arr.push({ year: `Yr ${y}`, Invested: Math.round(inv), Value: Math.round(val) });
    }
    return { data: arr, invested: inv, future: val };
  }, [monthly, years, rate]);

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-6">
        <div>
          <Label>Monthly SIP: <span className="font-semibold text-blue-900">{fmt(monthly)}</span></Label>
          <Slider data-testid="sip-slider-monthly" value={[monthly]} min={500} max={200000} step={500} onValueChange={(v) => setMonthly(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Duration: <span className="font-semibold text-blue-900">{years} years</span></Label>
          <Slider data-testid="sip-slider-years" value={[years]} min={1} max={40} step={1} onValueChange={(v) => setYears(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Expected return: <span className="font-semibold text-blue-900">{rate}% p.a.</span></Label>
          <Slider data-testid="sip-slider-rate" value={[rate]} min={4} max={18} step={0.5} onValueChange={(v) => setRate(v[0])} className="mt-3" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ResultStat label="You invest" value={fmt(invested)} />
          <ResultStat label="Future value" value={fmt(future)} accent />
        </div>
        <p className="text-xs text-slate-500">Estimated wealth gained: <span className="font-semibold text-emerald-600">{fmt(future - invested)}</span></p>
      </div>
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-6 bg-white soft-shadow h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
              <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.25}/><stop offset="95%" stopColor="#1E3A8A" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="year" fontSize={11} />
            <YAxis tickFormatter={(v) => (v >= 10000000 ? (v / 10000000).toFixed(1) + " Cr" : (v / 100000).toFixed(0) + " L")} fontSize={11} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Area type="monotone" dataKey="Value" stroke="#10B981" fill="url(#g1)" strokeWidth={2.5} />
            <Area type="monotone" dataKey="Invested" stroke="#1E3A8A" fill="url(#g2)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LumpsumCalc() {
  const [amount, setAmount] = useState(500000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const future = amount * Math.pow(1 + rate / 100, years);
  const data = Array.from({ length: years }).map((_, i) => ({
    year: `Yr ${i + 1}`,
    Value: Math.round(amount * Math.pow(1 + rate / 100, i + 1)),
    Principal: amount,
  }));
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-6">
        <div>
          <Label>Invested amount: <span className="font-semibold text-blue-900">{fmt(amount)}</span></Label>
          <Slider data-testid="ls-slider-amount" value={[amount]} min={10000} max={10000000} step={10000} onValueChange={(v) => setAmount(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Duration: <span className="font-semibold text-blue-900">{years} years</span></Label>
          <Slider data-testid="ls-slider-years" value={[years]} min={1} max={40} step={1} onValueChange={(v) => setYears(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Expected return: <span className="font-semibold text-blue-900">{rate}% p.a.</span></Label>
          <Slider data-testid="ls-slider-rate" value={[rate]} min={4} max={18} step={0.5} onValueChange={(v) => setRate(v[0])} className="mt-3" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ResultStat label="Principal" value={fmt(amount)} />
          <ResultStat label="Future value" value={fmt(future)} accent />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-6 bg-white soft-shadow h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="lg1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="year" fontSize={11} />
            <YAxis tickFormatter={(v) => (v >= 10000000 ? (v / 10000000).toFixed(1) + " Cr" : (v / 100000).toFixed(0) + " L")} fontSize={11} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Area type="monotone" dataKey="Value" stroke="#10B981" fill="url(#lg1)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RetirementCalc() {
  const [currentAge, setCurrentAge] = useState(32);
  const [retireAge, setRetireAge] = useState(60);
  const [monthlyExpense, setMonthlyExpense] = useState(60000);
  const [inflation, setInflation] = useState(6);
  const [returnRate, setReturnRate] = useState(11);
  const yearsToRetire = Math.max(retireAge - currentAge, 1);
  const futureMonthly = monthlyExpense * Math.pow(1 + inflation / 100, yearsToRetire);
  // corpus needed assuming 25-year retirement, 7% post-retirement returns, 6% inflation in retirement
  const corpusNeeded = (futureMonthly * 12 * 25); // simplified
  // required SIP to reach corpus
  const r = returnRate / 100 / 12;
  const n = yearsToRetire * 12;
  const sip = (corpusNeeded * r) / (Math.pow(1 + r, n) - 1);
  const data = Array.from({ length: yearsToRetire }).map((_, i) => {
    const yrs = i + 1;
    const inv = sip * 12 * yrs;
    let val = 0;
    for (let m = 0; m < yrs * 12; m++) val = (val + sip) * (1 + r);
    return { age: `Age ${currentAge + yrs}`, Invested: Math.round(inv), Corpus: Math.round(val) };
  });
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Current age</Label>
            <Input data-testid="ret-input-age" type="number" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} className="mt-1.5" />
          </div>
          <div>
            <Label>Retirement age</Label>
            <Input data-testid="ret-input-retire" type="number" value={retireAge} onChange={(e) => setRetireAge(Number(e.target.value))} className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label>Monthly expense today: <span className="font-semibold text-blue-900">{fmt(monthlyExpense)}</span></Label>
          <Slider data-testid="ret-slider-expense" value={[monthlyExpense]} min={20000} max={500000} step={5000} onValueChange={(v) => setMonthlyExpense(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Inflation: <span className="font-semibold text-blue-900">{inflation}%</span></Label>
          <Slider data-testid="ret-slider-inflation" value={[inflation]} min={3} max={10} step={0.5} onValueChange={(v) => setInflation(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Expected return: <span className="font-semibold text-blue-900">{returnRate}%</span></Label>
          <Slider data-testid="ret-slider-return" value={[returnRate]} min={6} max={15} step={0.5} onValueChange={(v) => setReturnRate(v[0])} className="mt-3" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ResultStat label="Future monthly need" value={fmt(futureMonthly)} />
          <ResultStat label="Corpus needed" value={fmt(corpusNeeded)} />
          <ResultStat label="Monthly SIP" value={fmt(sip)} accent />
          <ResultStat label="Years to invest" value={`${yearsToRetire} yrs`} />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-6 bg-white soft-shadow h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="rg1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.4}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="age" fontSize={11} />
            <YAxis tickFormatter={(v) => (v >= 10000000 ? (v / 10000000).toFixed(1) + " Cr" : (v / 100000).toFixed(0) + " L")} fontSize={11} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Area type="monotone" dataKey="Corpus" stroke="#10B981" fill="url(#rg1)" strokeWidth={2.5} />
            <Area type="monotone" dataKey="Invested" stroke="#1E3A8A" fill="#1E3A8A22" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TermCalc() {
  const [income, setIncome] = useState(1500000);
  const [loans, setLoans] = useState(2000000);
  const [age, setAge] = useState(32);
  const [dependents, setDependents] = useState(2);
  const ideal = Math.max(income * (age < 40 ? 18 : age < 50 ? 14 : 10), 5000000) + loans + dependents * 500000;
  const data = [
    { name: "10x Income", value: income * 10 },
    { name: "15x Income", value: income * 15 },
    { name: "Recommended", value: ideal },
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div>
          <Label>Annual income: <span className="font-semibold text-blue-900">{fmt(income)}</span></Label>
          <Slider data-testid="term-slider-income" value={[income]} min={300000} max={20000000} step={100000} onValueChange={(v) => setIncome(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Outstanding loans: <span className="font-semibold text-blue-900">{fmt(loans)}</span></Label>
          <Slider data-testid="term-slider-loans" value={[loans]} min={0} max={20000000} step={100000} onValueChange={(v) => setLoans(v[0])} className="mt-3" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Your age</Label>
            <Input data-testid="term-input-age" type="number" value={age} onChange={(e) => setAge(Number(e.target.value))} className="mt-1.5" />
          </div>
          <div>
            <Label>Dependents</Label>
            <Input data-testid="term-input-dep" type="number" value={dependents} onChange={(e) => setDependents(Number(e.target.value))} className="mt-1.5" />
          </div>
        </div>
        <ResultStat label="Recommended term cover" value={fmt(ideal)} accent />
        <p className="text-xs text-slate-500">Rule of thumb: 15-20x of annual income, plus liabilities, plus future goals.</p>
      </div>
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-6 bg-white soft-shadow h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" fontSize={12} />
            <YAxis tickFormatter={(v) => (v >= 10000000 ? (v / 10000000).toFixed(1) + " Cr" : (v / 100000).toFixed(0) + " L")} fontSize={11} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Bar dataKey="value" fill="#1E3A8A" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HealthCalc() {
  const [city, setCity] = useState("metro");
  const [age, setAge] = useState(35);
  const [familySize, setFamilySize] = useState(4);
  const baseByCity = city === "metro" ? 1500000 : city === "tier1" ? 1000000 : 700000;
  const ageMult = age >= 50 ? 1.6 : age >= 40 ? 1.3 : 1;
  const recommended = Math.round(baseByCity * ageMult * (familySize >= 4 ? 1.4 : familySize === 3 ? 1.2 : 1));
  const data = [
    { name: "Base", value: 500000 },
    { name: "Industry average", value: 1000000 },
    { name: "Recommended", value: recommended },
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div>
          <Label>City tier</Label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[
              { id: "metro", label: "Metro" },
              { id: "tier1", label: "Tier 1" },
              { id: "tier2", label: "Tier 2/3" },
            ].map((c) => (
              <button
                key={c.id}
                onClick={() => setCity(c.id)}
                data-testid={`health-city-${c.id}`}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  city === c.id ? "border-blue-900 bg-blue-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Eldest member age: <span className="font-semibold text-blue-900">{age}</span></Label>
          <Slider data-testid="health-slider-age" value={[age]} min={18} max={80} step={1} onValueChange={(v) => setAge(v[0])} className="mt-3" />
        </div>
        <div>
          <Label>Family size: <span className="font-semibold text-blue-900">{familySize}</span></Label>
          <Slider data-testid="health-slider-family" value={[familySize]} min={1} max={6} step={1} onValueChange={(v) => setFamilySize(v[0])} className="mt-3" />
        </div>
        <ResultStat label="Recommended cover" value={fmt(recommended)} accent />
        <p className="text-xs text-slate-500">Pair the base cover with a Super Top-Up of 2-3x for optimum cost vs. protection.</p>
      </div>
      <div className="rounded-2xl border border-slate-100 p-4 sm:p-6 bg-white soft-shadow h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" fontSize={12} />
            <YAxis tickFormatter={(v) => (v / 100000).toFixed(0) + " L"} fontSize={11} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Calculators() {
  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid="page-calculators">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl">
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50/60">Free calculators</Badge>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            Numbers that make <span className="text-gradient-blue">financial decisions easy.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed text-lg">
            Five calculators we actually use with our clients. No sign-up. No data stored.
          </p>
        </div>

        <Card className="mt-12 p-4 sm:p-8 soft-shadow border-slate-100">
          <Tabs defaultValue="sip">
            <TabsList className="bg-slate-100 p-1 rounded-xl h-auto flex-wrap">
              <TabsTrigger value="sip" data-testid="calc-tab-sip" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm">SIP</TabsTrigger>
              <TabsTrigger value="lumpsum" data-testid="calc-tab-lumpsum" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm">Lumpsum</TabsTrigger>
              <TabsTrigger value="retirement" data-testid="calc-tab-retirement" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm">Retirement</TabsTrigger>
              <TabsTrigger value="term" data-testid="calc-tab-term" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm">Term Cover</TabsTrigger>
              <TabsTrigger value="health" data-testid="calc-tab-health" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm">Health Cover</TabsTrigger>
            </TabsList>
            <TabsContent value="sip" className="pt-8"><SipCalc /></TabsContent>
            <TabsContent value="lumpsum" className="pt-8"><LumpsumCalc /></TabsContent>
            <TabsContent value="retirement" className="pt-8"><RetirementCalc /></TabsContent>
            <TabsContent value="term" className="pt-8"><TermCalc /></TabsContent>
            <TabsContent value="health" className="pt-8"><HealthCalc /></TabsContent>
          </Tabs>
        </Card>
      </section>
    </div>
  );
}
